/* eslint-disable prettier/prettier */
import { useMutation } from '@apollo/react-hooks';
import { pathOr, isEmpty } from 'ramda';
import { useTranslation } from 'react-i18next';

import { CREATE_CHAT_MESSAGE } from 'apollo/mutations';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { KEYS, USER_ROLES } from 'components/evieOnboarding/utils/constants';
import { useGetParams } from 'components/evieOnboarding/utils/useGetParams';
import showNotification from 'utils/showNotification';
import { handleTrackEvent } from 'components/evieOnboarding/utils/handleTrackEvent';
import { useEvieChatContext } from './EvieChatProvider';

export const useCreateChatMessage = ({ updateUserMessage = () => { }, setNewMessage = () => { } }) => {
  const { t } = useTranslation();
  const [createChatMessage] = useMutation(CREATE_CHAT_MESSAGE);
  const [userToken] = useLocalStorage(KEYS.USER_TOKEN);
  const { urlToken } = useGetParams();
  const token = userToken || urlToken;
  const { toggleIsAwaitingResponse, refetchChatHistory, isQuotaExceeded } = useEvieChatContext();

  const sendMessage = async (event, newMessage) => {
    event.preventDefault();
    if (isQuotaExceeded) return;

    toggleIsAwaitingResponse();
    updateUserMessage(newMessage);
    setNewMessage('');
    try {
      const response = await createChatMessage({
        variables: { message: newMessage },
        context: {
          headers: {
            authorization: token ? `Bearer ${token}` : '',
            role: USER_ROLES.INDIVIDUAL,
          },
        },
      });

      const chatAction = pathOr('', ['data', 'createChatMessage', 'chatAction'])(response);
      const createChatMessageErrors = pathOr([], ['data', 'createChatMessage', 'errors'])(response);
      const contentRecommendations = pathOr([], ['data', 'createChatMessage', 'contentRecommendations'])(response);
      const emptyErrors = isEmpty(createChatMessageErrors);

      if (emptyErrors) {
        if (chatAction) {
          const { type, key, trigger } = chatAction;
          handleTrackEvent('Chat action shown', { type, key, trigger });
        }
        contentRecommendations.forEach(({ id, contentType, title }) => {
          handleTrackEvent('Recommendation shown', { type: contentType, id, title });
        });
        await refetchChatHistory();
        updateUserMessage('');
        toggleIsAwaitingResponse();
      } else {
        await refetchChatHistory();
        toggleIsAwaitingResponse();
        console.error('SERVER_ERROR: ', createChatMessageErrors);
        showNotification({
          messages: createChatMessageErrors || [t('evieApp.assistant.errors.sendMessage')],
        });
      }
    } catch (error) {
      toggleIsAwaitingResponse();
      console.error('SOMETHING_WENT_WRONG: ', error);
      showNotification({
        messages: [t('evieApp.assistant.errors.connectionError')],
      });
    }
  };

  return { sendMessage };
};
